import { type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button as HeadlessButton } from '@headlessui/react';
import { tv, type VariantProps } from 'tailwind-variants';

import { buttonVariantClasses } from '../lib/colorSchemes';

/**
 * Background and hover-background are driven by CSS custom properties
 * (--btn-bg / --btn-hover-bg) set by compound variants. This avoids
 * tailwind-merge stripping hover classes when a matching base bg class
 * exists in the same compound variant string.
 */
const buttonVariants = tv({
  base: [
    'inline-flex items-center justify-center whitespace-nowrap',
    'rounded-md font-medium transition-colors',
    'border border-transparent',
    'bg-[var(--btn-bg,transparent)] hover:bg-[var(--btn-hover-bg,transparent)]',
    '[outline:none] focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50'
  ],
  variants: {
    variant: {
      default: '',
      outline: '',
      ghost: '',
      quiet: '',
      subtle: ''
    },
    color: {
      primary: '',
      secondary: '',
      destructive: '',
      warning: '',
      neutral: ''
    },
    size: {
      xs: 'h-6 gap-1 px-2 text-xs',
      sm: 'h-7 gap-1.5 px-3 text-sm',
      md: 'h-8 gap-2 px-4 text-sm',
      lg: 'h-9 gap-2 px-5 text-base',
      xl: 'h-10 gap-2 px-6 text-base',
      '2xl': 'h-11 gap-2.5 px-7 text-lg',
      '3xl': 'h-12 gap-3 px-8 text-xl',
      'icon-xs': 'h-6 w-6 p-0 text-xs',
      'icon-sm': 'h-7 w-7 p-0 text-sm',
      'icon-md': 'h-8 w-8 p-0 text-sm',
      'icon-lg': 'h-9 w-9 p-0 text-base',
      'icon-xl': 'h-10 w-10 p-0 text-base'
    }
  },
  compoundVariants: [
    // ── default (filled) ────────────────────────────────────────────────────
    { variant: 'default', color: 'primary', class: buttonVariantClasses.primary.default },
    { variant: 'default', color: 'secondary', class: buttonVariantClasses.secondary.default },
    { variant: 'default', color: 'destructive', class: buttonVariantClasses.destructive.default },
    { variant: 'default', color: 'warning', class: buttonVariantClasses.warning.default },
    { variant: 'default', color: 'neutral', class: buttonVariantClasses.neutral.default },
    // ── outline ─────────────────────────────────────────────────────────────
    { variant: 'outline', color: 'primary', class: buttonVariantClasses.primary.outline },
    { variant: 'outline', color: 'secondary', class: buttonVariantClasses.secondary.outline },
    { variant: 'outline', color: 'destructive', class: buttonVariantClasses.destructive.outline },
    { variant: 'outline', color: 'warning', class: buttonVariantClasses.warning.outline },
    { variant: 'outline', color: 'neutral', class: buttonVariantClasses.neutral.outline },
    // ── ghost ────────────────────────────────────────────────────────────────
    { variant: 'ghost', color: 'primary', class: buttonVariantClasses.primary.ghost },
    { variant: 'ghost', color: 'secondary', class: buttonVariantClasses.secondary.ghost },
    { variant: 'ghost', color: 'destructive', class: buttonVariantClasses.destructive.ghost },
    { variant: 'ghost', color: 'warning', class: buttonVariantClasses.warning.ghost },
    { variant: 'ghost', color: 'neutral', class: buttonVariantClasses.neutral.ghost },
    // ── quiet (muted until hover) ────────────────────────────────────────────
    { variant: 'quiet', color: 'primary', class: buttonVariantClasses.primary.quiet },
    { variant: 'quiet', color: 'secondary', class: buttonVariantClasses.secondary.quiet },
    { variant: 'quiet', color: 'destructive', class: buttonVariantClasses.destructive.quiet },
    { variant: 'quiet', color: 'warning', class: buttonVariantClasses.warning.quiet },
    { variant: 'quiet', color: 'neutral', class: buttonVariantClasses.neutral.quiet },
    // ── subtle ───────────────────────────────────────────────────────────────
    { variant: 'subtle', color: 'primary', class: buttonVariantClasses.primary.subtle },
    { variant: 'subtle', color: 'secondary', class: buttonVariantClasses.secondary.subtle },
    { variant: 'subtle', color: 'destructive', class: buttonVariantClasses.destructive.subtle },
    { variant: 'subtle', color: 'warning', class: buttonVariantClasses.warning.subtle },
    { variant: 'subtle', color: 'neutral', class: buttonVariantClasses.neutral.subtle }
  ],
  defaultVariants: {
    variant: 'default',
    color: 'secondary',
    size: 'md'
  }
});

type ButtonVariants = VariantProps<typeof buttonVariants>;

interface ButtonProps
  extends ButtonVariants, Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  leftIcon?: IconDefinition;
  rightIcon?: IconDefinition;
  isLoading?: boolean;
}

function Button({
  variant,
  color,
  size,
  leftIcon,
  rightIcon,
  isLoading = false,
  disabled = false,
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <HeadlessButton
      {...props}
      disabled={disabled || isLoading}
      className={buttonVariants({ variant, color, size, className })}>
      {isLoading ? (
        <FontAwesomeIcon icon={faSpinner} spin className="shrink-0" />
      ) : (
        leftIcon && <FontAwesomeIcon icon={leftIcon} className="shrink-0" />
      )}
      {children}
      {!isLoading && rightIcon && <FontAwesomeIcon icon={rightIcon} className="shrink-0" />}
    </HeadlessButton>
  );
}

export { Button, buttonVariants, type ButtonProps, type ButtonVariants };
